import React, { useEffect, useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LightModeIcon from '@mui/icons-material/LightMode';
import List from '@mui/material/List';
import ListIcon from '@mui/icons-material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

const drawerWidth = 240;
const navItems = [['Expertise', 'expertise'], ['History', 'history'], ['Projects', 'projects'], ['Interests', 'interests'], ['Contact', 'contact']];

function Navigation({parentToChild, modeChange}: any) {

  const {mode} = parentToChild;

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [logoShake, setLogoShake] = useState<boolean>(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        const scrolled = window.scrollY > navbar.clientHeight;
        setScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (section: string) => {
    console.log(section)
    const expertiseElement = document.getElementById(section);
    if (expertiseElement) {
      expertiseElement.scrollIntoView({ behavior: 'smooth' });
      console.log('Scrolling to:', expertiseElement);  // Debugging: Ensure the element is found
    } else {
      console.error('Element with id "expertise" not found');  // Debugging: Log error if element is not found
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Trigger shake animation
    setLogoShake(true);
    setTimeout(() => setLogoShake(false), 500); // Reset after animation completes
  };

  const drawer = (
    <Box 
      className="navigation-bar-responsive" 
      onClick={handleDrawerToggle} 
      sx={{ 
        textAlign: 'center',
        backgroundColor: mode === 'dark' ? '#0a0d12' : '#fff',
        height: '100%'
      }}
    >
      <Box 
        className="mobile-menu-top" 
        sx={{ 
          padding: '12px 0',
          color: mode === 'dark' ? 'white' : '#0d1116',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '5px'
        }}
      >
        <ListIcon sx={{ color: mode === 'dark' ? 'white' : '#0d1116' }} />
        <Typography component="span" sx={{ fontWeight: 'bold' }}>Menu</Typography>
      </Box>
      <Divider sx={{ borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
      <List>
        {navItems.map((item) => (
          <ListItem key={item[0]} disablePadding>
            <ListItemButton 
              sx={{ 
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                }
              }} 
              onClick={() => scrollToSection(item[1])}
            >
              <ListItemText 
                primary={item[0]} 
                primaryTypographyProps={{ 
                  sx: { 
                    color: mode === 'dark' ? 'white' : '#0d1116',
                    fontFamily: '"Lato", sans-serif'
                  } 
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`}>
        <Toolbar className='navigation-bar' sx={{ justifyContent: 'space-between' }}>
          {/* Left section with menu and theme switcher */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Theme Switcher */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {mode === 'dark' ? (
                <LightModeIcon onClick={() => modeChange()} style={{ cursor: 'pointer' }}/>
              ) : (
                <DarkModeIcon onClick={() => modeChange()} style={{ cursor: 'pointer' }}/>
              )}
            </Box>
          </Box>
          
          {/* Centered Logo */}
          <Typography
            variant="h1"
            component="h1"
            onClick={scrollToTop}
            sx={{
              position: 'absolute',
              left: '50%',
              transform: `translateX(-50%) ${logoShake ? 'translateX(-5px)' : ''}`,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 700,
              color: '#fff',
              fontFamily: '"Lato", sans-serif',
              letterSpacing: '0.5px',
              textShadow: mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
              zIndex: 1,
              cursor: 'pointer',
              transition: 'transform 0.1s ease-in-out',
              animation: logoShake ? 'shake 0.5s' : 'none',
              '@keyframes shake': {
                '0%': { transform: 'translateX(-50%)' },
                '20%': { transform: 'translateX(calc(-50% - 3px))' },
                '40%': { transform: 'translateX(calc(-50% + 3px))' },
                '60%': { transform: 'translateX(calc(-50% - 3px))' },
                '80%': { transform: 'translateX(calc(-50% + 3px))' },
                '100%': { transform: 'translateX(-50%)' },
              },
              '&:hover': {
                opacity: 0.9,
              }
            }}
          >
            &lt;/df&gt;
          </Typography>
          
          {/* Right section with nav items */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map((item) => (
              <Button key={item[0]} onClick={() => scrollToSection(item[1])} sx={{ color: '#fff' }}>
                {item[0]}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: mode === 'dark' ? '#0a0d12' : '#fff',
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default Navigation;